import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Capacitor } from '@capacitor/core';

const QRScanner = ({ isOpen, onClose, onScan }) => {
    const videoRef = useRef(null);
    const [stream, setStream] = useState(null);
    const [error, setError] = useState('');
    const [scanning, setScanning] = useState(false);
    const scanIntervalRef = useRef(null);

    useEffect(() => {
        if (isOpen) {
            startCamera();
        } else {
            stopCamera();
        }

        return () => {
            stopCamera();
        };
    }, [isOpen]);

    const startCamera = async () => {
        try {
            // Request camera permission on mobile
            if (Capacitor.isNativePlatform()) {
                const permission = await navigator.permissions.query({ name: 'camera' });
                if (permission.state === 'denied') {
                    setError('Camera permission denied. Please enable camera access in your device settings.');
                    return;
                }
            }

            const mediaStream = await navigator.mediaDevices.getUserMedia({
                video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } }
            });
            setStream(mediaStream);
            if (videoRef.current) {
                videoRef.current.srcObject = mediaStream;
                await videoRef.current.play();
                setScanning(true);
                startScanning();
            }
        } catch (err) {
            console.error('Camera access error:', err);
            if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
                setError('Camera permission denied. Please enable camera access in your device settings.');
            } else if (err.name === 'NotFoundError') {
                setError('No camera found on this device.');
            } else {
                setError('Unable to access camera. Please grant camera permissions.');
            }
        }
    };

    const stopCamera = () => {
        if (stream) {
            stream.getTracks().forEach(track => track.stop());
            setStream(null);
        }
        if (scanIntervalRef.current) {
            clearInterval(scanIntervalRef.current);
        }
        setScanning(false);
    };

    const startScanning = async () => {
        // Check if BarcodeDetector is supported
        if ('BarcodeDetector' in window) {
            try {
                const barcodeDetector = new window.BarcodeDetector({ formats: ['qr_code'] });
                
                scanIntervalRef.current = setInterval(async () => {
                    if (videoRef.current && videoRef.current.readyState === videoRef.current.HAVE_ENOUGH_DATA) {
                        try {
                            const barcodes = await barcodeDetector.detect(videoRef.current);
                            if (barcodes.length > 0) {
                                handleQRCodeDetected(barcodes[0].rawValue);
                            }
                        } catch (err) {
                            console.error('Barcode detection error:', err);
                        }
                    }
                }, 300);
            } catch (err) {
                console.error('BarcodeDetector error:', err);
                setError('QR code scanning not supported on this device.');
            }
        } else {
            setError('QR code scanning not supported on this browser. Please use Chrome or Edge.');
        }
    };

    const handleQRCodeDetected = (data) => {
        try {
            // Extract restaurant ID and table ID from QR code URL
            // Expected format: https://restroai.in/menu/{restaurantId}/{tableId}
            const url = new URL(data);
            const pathParts = url.pathname.split('/').filter(part => part);
            
            // Find 'menu' in path and get the next two parts
            const menuIndex = pathParts.indexOf('menu');
            if (menuIndex !== -1 && pathParts.length >= menuIndex + 3) {
                const restaurantId = pathParts[menuIndex + 1];
                const tableId = pathParts[menuIndex + 2];
                
                if (restaurantId && tableId) {
                    stopCamera();
                    onScan({ restaurantId, tableId, url: data });
                }
            }
        } catch (err) {
            console.error('Invalid QR code:', err);
        }
    };

    if (!isOpen) return null;

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-sm"
                onClick={onClose}
            >
                <motion.div
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.9, opacity: 0 }}
                    className="relative w-full max-w-md mx-4"
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Header */}
                    <div className="bg-gradient-to-r from-blue-500 to-cyan-500 rounded-t-2xl p-4">
                        <div className="flex items-center justify-between">
                            <h3 className="text-white font-semibold text-lg">Scan QR Code</h3>
                            <button
                                onClick={onClose}
                                className="text-white hover:bg-white/20 rounded-full p-2 transition-colors"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                    </div>

                    {/* Scanner Area */}
                    <div className="bg-white/10 backdrop-blur-xl rounded-b-2xl p-6 border-x border-b border-white/20">
                        {error ? (
                            <div className="text-center py-8">
                                <div className="text-red-400 mb-4">
                                    <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                    </svg>
                                </div>
                                <p className="text-white/80 mb-4">{error}</p>
                                <button
                                    onClick={startCamera}
                                    className="bg-gradient-to-r from-blue-500 to-cyan-500 text-white px-6 py-2 rounded-full font-semibold hover:from-blue-600 hover:to-cyan-600 transition-all"
                                >
                                    Try Again
                                </button>
                            </div>
                        ) : (
                            <div className="relative">
                                {/* Video Element */}
                                <video
                                    ref={videoRef}
                                    className="w-full rounded-xl"
                                    playsInline
                                    muted
                                    autoPlay
                                />

                                {/* Scanning Overlay */}
                                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                    <div className="relative w-64 h-64">
                                        {/* Corner Brackets */}
                                        <div className="absolute top-0 left-0 w-8 h-8 border-t-4 border-l-4 border-blue-400 rounded-tl-lg"></div>
                                        <div className="absolute top-0 right-0 w-8 h-8 border-t-4 border-r-4 border-blue-400 rounded-tr-lg"></div>
                                        <div className="absolute bottom-0 left-0 w-8 h-8 border-b-4 border-l-4 border-blue-400 rounded-bl-lg"></div>
                                        <div className="absolute bottom-0 right-0 w-8 h-8 border-b-4 border-r-4 border-blue-400 rounded-br-lg"></div>
                                        
                                        {/* Scanning Line */}
                                        <motion.div
                                            className="absolute left-0 right-0 h-1 bg-gradient-to-r from-transparent via-blue-400 to-transparent"
                                            animate={{ top: ['0%', '100%'] }}
                                            transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
                                        />
                                    </div>
                                </div>

                                {/* Instructions */}
                                <div className="mt-4 text-center">
                                    <p className="text-white/80 text-sm">
                                        Position the QR code within the frame
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
};

export default QRScanner;
